import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigationItems, type NavItem } from "../hooks/useNavigationItems";
import { motion } from "framer-motion";
import { cn } from "../lib/utils";
import {
  Home,
  Network,
  GraduationCap,
  Orbit,
  FlaskConical,
  Check, Blocks, MonitorPlay,
  MessageSquareText, LibraryBig,
  LayoutList, Route, Store,
  Languages,
} from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "./ui/tooltip";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "./ui/hover-card";
import { useDirection } from "../hooks/useDirection";
import { getSpaceIcon } from "./spaces/utils/spaceIconMap";
import { useSpaces } from "../context/SpaceProvider";

const AppRail = () => {
  const { isRTL } = useDirection();
  const {
    isLoggedIn,
    isMentor,
    isVendor,
    showGroups,
    activeItem,
    unreadChatsCount,
    navigateToPortalTab,
    spaces,
    hasSpaces,
    selectedSpace,
    setActiveSpace,
    isOnSpaces,
    navigate,
    t,
  } = useNavigationItems();

  const tooltipSide = isRTL ? ("left" as const) : ("right" as const);

  const dashboardItems: NavItem[] = [
    {
      id: "portal_my_workspace",
      icon: Blocks,
      label: t("workspace"),
      show: true,
      onClick: () => navigateToPortalTab("my_workspace"),
    },
    {
      id: "portal_activities",
      icon: MonitorPlay,
      label: t("activities"),
      show: true,
      onClick: () => navigateToPortalTab("activities"),
    },
    {
      id: "programs",
      icon: LayoutList,
      label: t("programs") || "Programs",
      show: isLoggedIn,
      onClick: () => navigate("/programs"),
    },
    {
      id: "translations",
      icon: Languages,
      label: t("translations") || "Translations",
      show: isLoggedIn,
      onClick: () => navigate("/translations"),
    },
  ];

  const discoverItems: NavItem[] = [
    {
      id: "marketplace",
      icon: LibraryBig,
      label: t("marketplace"),
      show: true,
      onClick: () => navigate("/marketplace"),
    },
    {
      id: "science",
      icon: FlaskConical,
      label: t("science"),
      show: true,
      onClick: () => navigate("/science"),
    },
  ];

  const bottomItems: NavItem[] = [
    {
      id: "mentoring",
      icon: GraduationCap,
      label: t("mentoring") || "Mentoring",
      show: isMentor,
      onClick: () => navigate("/mentoring"),
    },
    {
      id: "portal_my_groups",
      icon: Network,
      label: t("groups"),
      show: isLoggedIn && showGroups,
      onClick: () => navigateToPortalTab("my_groups"),
    },
    {
      id: "store",
      icon: Store,
      label: t("nav_store", "Store"),
      show: isVendor,
      onClick: () => navigate("/commerce"),
    },
  ];

  const SPACES_ACCENT = "#6366f1";

  const spacesIcon = isOnSpaces && selectedSpace
    ? getSpaceIcon(selectedSpace.icon)
    : Orbit;
  const spacesColor = isOnSpaces && selectedSpace
    ? selectedSpace.color || SPACES_ACCENT
    : SPACES_ACCENT;

  return (
    <TooltipProvider delayDuration={200}>
      <nav className="w-[72px] shrink-0 border-e border-gray-200/50 bg-white flex flex-col items-center pt-4 gap-1 overflow-y-auto overflow-x-hidden shadow-[1px_0_0_rgba(79,70,229,0.04)]">
        {/* Home */}
        <RailButton
          icon={Home}
          label={t("home")}
          isActive={activeItem === "home"}
          onClick={() => navigate("/home")}
          tooltipSide={tooltipSide}
        />

        {/* Dashboard items — auth only */}
        {isLoggedIn && (
          <>
            <div className="w-8 h-px bg-gray-100 mt-2 mb-1" />
            {dashboardItems
              .filter((item) => item.show)
              .map((item) => (
                <RailButton
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  isActive={activeItem === item.id}
                  onClick={item.onClick}
                  tooltipSide={tooltipSide}
                />
              ))}

            {/* Spaces — moved up from the pinned footer into the main list */}
            {hasSpaces ? (
              <SpacesRailButton
                icon={spacesIcon}
                label={isOnSpaces && selectedSpace ? selectedSpace.name : t("spaces")}
                isActive={isOnSpaces}
                accentColor={spacesColor}
                tooltipSide={tooltipSide}
                spaces={spaces}
                selectedSpace={selectedSpace}
                onClickSpaces={() => navigate("/spaces")}
                onSelectSpace={(spaceId) => {
                  setActiveSpace(spaceId);
                  window.dispatchEvent(
                    new CustomEvent("spaces-select-space", { detail: spaceId }),
                  );
                  if (!isOnSpaces)
                    navigate(`/spaces?space=${spaceId}`, {
                      state: { selectedSpaceId: spaceId },
                    });
                }}
              />
            ) : (
              <RailButton
                icon={Orbit}
                label={t("spaces")}
                isActive={isOnSpaces}
                accentColor={SPACES_ACCENT}
                layoutGroup="spaces"
                onClick={() => navigate("/spaces")}
                tooltipSide={tooltipSide}
              />
            )}

            {/* Studio (formerly PWFs) */}
            <RailButton
              icon={Route}
              label={t("nav_studio", "Studio")}
              isActive={activeItem === "studio"}
              onClick={() => navigate("/pwf")}
              tooltipSide={tooltipSide}
            />
          </>
        )}

        {/* Chat — auth only */}
        {isLoggedIn && (
          <>
            <div className="w-8 h-px bg-gray-100 mt-2 mb-1" />
            <RailButton
              icon={MessageSquareText}
              label={t("chats")}
              isActive={activeItem === "chats"}
              onClick={() => navigate("/chats")}
              tooltipSide={tooltipSide}
              badge={unreadChatsCount}
            />
          </>
        )}

        {/* Discover section */}
        <div className="w-8 h-px bg-gray-100 mt-2 mb-1" />
        <span className="text-[10px] uppercase text-muted-foreground/50 font-semibold tracking-widest mb-0.5 select-none">
          {t("discover")}
        </span>
        {discoverItems.map((item) => (
          <RailButton
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={activeItem === item.id}
            onClick={item.onClick}
            tooltipSide={tooltipSide}
          />
        ))}

        {/* Spacer pushes Mentoring/Groups to bottom */}
        <div className="flex-1" />

        {/* Mentoring / Groups — relevant once other people are involved */}
        {bottomItems.filter((item) => item.show).length > 0 && (
          <>
            <div className="w-8 h-px bg-gray-100 mb-1" />
            {bottomItems
              .filter((item) => item.show)
              .map((item) => (
                <RailButton
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  isActive={activeItem === item.id}
                  onClick={item.onClick}
                  tooltipSide={tooltipSide}
                />
              ))}
          </>
        )}

        <div className="pb-4" />
      </nav>
    </TooltipProvider>
  );
};

// ─── Spaces Rail Button with hover dropdown ─────────────────────────────────

interface SpacesRailButtonProps {
  icon: typeof Home;
  label: string;
  isActive: boolean;
  accentColor: string;
  tooltipSide: "left" | "right";
  spaces: ReturnType<typeof useSpaces>["spaces"];
  selectedSpace: ReturnType<typeof useSpaces>["activeSpace"];
  onClickSpaces: () => void;
  onSelectSpace: (spaceId: string) => void;
}

const SpacesRailButton = ({
  icon: Icon,
  label,
  isActive,
  accentColor,
  tooltipSide,
  spaces,
  selectedSpace,
  onClickSpaces,
  onSelectSpace,
}: SpacesRailButtonProps) => {
  const { t } = useTranslation();

  const buttonStyle: React.CSSProperties = isActive
    ? { backgroundColor: `${accentColor}15`, color: accentColor }
    : { color: accentColor };

  const indicatorStyle: React.CSSProperties = { backgroundColor: accentColor };

  const hoverSide = tooltipSide;

  return (
    <HoverCard openDelay={300} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          onClick={onClickSpaces}
          className={cn(
            "group relative flex items-center justify-center w-[48px] h-[48px] rounded-xl transition-all duration-200",
            !isActive && "bg-transparent hover:bg-primary/8",
          )}
          style={buttonStyle}
        >
          {isActive && (
            <motion.div
              layoutId="app-rail-spaces-indicator"
              className="absolute inset-y-1.5 start-0 w-[3px] rounded-full"
              style={indicatorStyle}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <Icon className="w-5 h-5" />
        </button>
      </HoverCardTrigger>

      <HoverCardContent
        side={hoverSide}
        sideOffset={12}
        align="end"
        className="w-56 p-1.5"
      >
        <div className="space-y-0.5">
          <span className="block px-2.5 py-1.5 text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider select-none">
            {t("spaces")}
          </span>
          {spaces.map((space) => {
            const SpaceIcon = getSpaceIcon(space.icon);
            const isSelected = selectedSpace?._id === space._id;

            return (
              <button
                key={space._id}
                onClick={() => onSelectSpace(space._id)}
                className={cn(
                  "flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-start transition-colors",
                  isSelected
                    ? "bg-accent"
                    : "hover:bg-accent/50",
                )}
              >
                <SpaceIcon
                  className="w-4 h-4 shrink-0"
                  style={{ color: space.color }}
                />
                <span className="text-sm font-medium text-foreground truncate flex-1">
                  {space.name}
                </span>
                {isSelected && (
                  <Check className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

// ─── Standard Rail Button ───────────────────────────────────────────────────

interface RailButtonProps {
  icon: typeof Home;
  label: string;
  isActive: boolean;
  onClick: () => void;
  tooltipSide: "left" | "right";
  accentColor?: string;
  layoutGroup?: "main" | "spaces";
  badge?: number;
}

const RailButton = ({
  icon: Icon,
  label,
  isActive,
  onClick,
  tooltipSide,
  accentColor,
  layoutGroup = "main",
  badge = 0,
}: RailButtonProps) => {
  const layoutId =
    layoutGroup === "spaces" ? "app-rail-spaces-indicator" : "app-rail-indicator";

  const buttonStyle: React.CSSProperties | undefined = accentColor
    ? isActive
      ? { backgroundColor: `${accentColor}15`, color: accentColor }
      : { color: `${accentColor}99` }
    : undefined;

  const indicatorStyle: React.CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor }
    : undefined;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            "group relative flex items-center justify-center w-[48px] h-[48px] rounded-xl transition-all duration-200",
            !accentColor && (
              isActive
                ? "bg-primary/10 text-primary"
                : "bg-transparent text-muted-foreground/50 hover:text-primary hover:bg-primary/8"
            ),
            accentColor && !isActive && "bg-transparent hover:bg-primary/8",
          )}
          style={buttonStyle}
        >
          {isActive && (
            <motion.div
              layoutId={layoutId}
              className={cn(
                "absolute inset-y-1.5 start-0 w-[3px] rounded-full",
                !accentColor && "bg-primary",
              )}
              style={indicatorStyle}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <Icon className="w-5 h-5" />
          {badge > 0 && (
            <span className="absolute -top-1 -end-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export default AppRail;
